import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { PlanCard } from '@/components/PlanCard';
import { api } from '@/lib/api';
import { PLATFORM_ACCENTS, formatINR, formatNumber } from '@/lib/format';
import { platformLogo } from '@/lib/logos';
import { ArrowRight, Check, AlertCircle } from 'lucide-react';

export default function PlatformDetailPage() {
    const { platform } = useParams();
    const [data, setData] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        api.get(`/platforms/${platform}`).then(r => setData(r.data)).catch(() => setData({ error: true }));
    }, [platform]);

    if (!data) return <PublicLayout><div className="p-10 text-center text-muted-foreground">Loading…</div></PublicLayout>;
    if (data.error) return <PublicLayout><div className="p-10 text-center text-muted-foreground">Platform not found</div></PublicLayout>;

    const accent = PLATFORM_ACCENTS[platform] || PLATFORM_ACCENTS.olaparty;
    const p = data.platform;

    return (
        <PublicLayout>
            <section style={{ background: accent.bg }}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                    <div className="lg:col-span-7">
                        <div className="inline-flex items-center gap-2 text-xs font-medium px-3 py-1 rounded-full bg-white border" style={{ borderColor: accent.border, color: accent.color }}>Top-up service</div>
                        <div className="mt-3 flex items-center gap-3">
                            {platformLogo(p.key) && (
                                <img src={platformLogo(p.key)} alt={p.name} className="h-14 w-14 rounded-2xl object-cover shadow-card" style={{ border: `1px solid ${accent.border}` }} />
                            )}
                            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold" style={{ color: '#0B1220' }}>{p.name}</h1>
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">{p.tagline}</div>
                        <p className="mt-4 text-base text-foreground/85 max-w-2xl">{p.description}</p>
                        <ul className="mt-4 space-y-1.5">
                            {(p.highlights || []).map((h, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-foreground/90">
                                    <Check className="h-4 w-4 mt-0.5" style={{ color: accent.color }} /> {h}
                                </li>
                            ))}
                        </ul>
                        <div className="mt-6 flex gap-3 flex-wrap">
                            <Button onClick={() => navigate(`/register`)} className="bg-[#0E2A47] hover:bg-[#0B223A]" data-testid={`platform-detail-join-button-${p.key}`}>
                                Join Now <ArrowRight className="h-4 w-4 ml-1" />
                            </Button>
                            <Button variant="outline" onClick={() => navigate(`/recharge?platform=${p.key}`)} data-testid={`platform-detail-recharge-button-${p.key}`}>
                                Go to Recharge
                            </Button>
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <Card className="p-5 shadow-card">
                            <div className="text-xs uppercase tracking-wider text-muted-foreground">{p.unit_symbol} unit</div>
                            <div className="font-display text-2xl font-semibold mt-1" style={{ color: accent.color }}>{p.name} {p.unit}</div>
                            <div className="mt-3 text-sm text-muted-foreground">{data.plans.length} plans available - from {formatINR(data.plans[0]?.price_inr || 0)} to {formatINR(data.plans[data.plans.length - 1]?.price_inr || 0)}.</div>
                            <div className="mt-4 grid grid-cols-2 gap-2">
                                {data.plans.slice(0, 4).map((pl) => (
                                    <div key={pl.id} className="p-3 rounded-xl border border-border">
                                        <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{formatINR(pl.price_inr)}</div>
                                        <div className="font-display font-semibold tabular-nums">{formatNumber(pl.amount)}</div>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="flex items-end justify-between mb-5">
                    <h2 className="font-display text-2xl font-semibold">{p.name} recharge plans</h2>
                    <Button asChild variant="outline" data-testid="platform-detail-all-plans-button"><Link to={`/recharge?platform=${p.key}`}>Recharge with selected plan</Link></Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {data.plans.map((pl) => (
                        <PlanCard key={pl.id} plan={pl} platform={p.key} unitLabel={p.unit} onSelect={() => navigate(`/recharge?platform=${p.key}&plan=${pl.id}`)} />
                    ))}
                </div>
                <div className="mt-6 p-4 rounded-xl border" style={{ background: '#FFF7ED', borderColor: '#FED7AA' }}>
                    <div className="flex items-start gap-2 text-[13px]">
                        <AlertCircle className="h-4 w-4 mt-0.5 text-[#B45309]" />
                        <div className="text-[#7C2D12]">Digital goods are non-reversible once credited. For entertainment use only - not for gambling or money games.</div>
                    </div>
                </div>
            </section>
        </PublicLayout>
    );
}
